import type { DiskUploadRecord } from '../Contracts/DiskUpload'

/**
 * Незавершені сесії шматків у IndexedDB — щоб після перезавантаження
 * сторінки докачати файл, а не починати заново.
 *
 * Сховище — зручність одного браузера: у приватному вікні, із заблокованими
 * даними сайту чи без IndexedDB кожен виклик тихо нічого не робить, і
 * завантаження працюють як і раніше, лише без відновлення.
 */
const DB_NAME = 'disk-uploads'
const DB_VERSION = 1
const STORE = 'sessions'

let opening: Promise<IDBDatabase | null> | null = null

const open = (): Promise<IDBDatabase | null> => {
    if (opening) return opening

    opening = new Promise<IDBDatabase | null>((resolve) => {
        try {
            if (typeof indexedDB === 'undefined') {
                resolve(null)

                return
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION)

            request.onupgradeneeded = () => {
                const db = request.result

                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'uploadUuid' })
            }
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => resolve(null)
            request.onblocked = () => resolve(null)
        } catch {
            resolve(null)
        }
    })

    // Невдале відкриття не кешуємо: наступна спроба може вдатися
    void opening.then((db) => {
        if (db === null) opening = null
    })

    return opening
}

const run = async <T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
    fallback: T
): Promise<T> => {
    try {
        const db = await open()
        if (db === null) return fallback

        return await new Promise<T>((resolve) => {
            try {
                const request = action(db.transaction(STORE, mode).objectStore(STORE))

                request.onsuccess = () => resolve(request.result)
                request.onerror = () => resolve(fallback)
            } catch {
                resolve(fallback)
            }
        })
    } catch {
        return fallback
    }
}

const isRecord = (value: unknown): value is DiskUploadRecord => {
    const raw = value as Partial<DiskUploadRecord> | null

    return (
        raw !== null &&
        typeof raw === 'object' &&
        typeof raw.uploadUuid === 'string' &&
        typeof raw.ownerKey === 'string' &&
        typeof raw.parent === 'string' &&
        typeof raw.name === 'string' &&
        typeof raw.size === 'number' &&
        typeof raw.lastModified === 'number'
    )
}

export const saveUploadRecord = async (record: DiskUploadRecord): Promise<void> => {
    await run<IDBValidKey>('readwrite', (store) => store.put(record), '')
}

export const deleteUploadRecord = async (uploadUuid: string): Promise<void> => {
    await run<undefined>('readwrite', (store) => store.delete(uploadUuid), undefined)
}

/** Записи цього користувача, найстаріші першими. */
export const listUploadRecords = async (ownerKey: string): Promise<DiskUploadRecord[]> => {
    const all = await run<unknown[]>('readonly', (store) => store.getAll(), [])

    return all
        .filter(isRecord)
        .filter((record) => record.ownerKey === ownerKey)
        .sort((a, b) => a.createdAt - b.createdAt)
}
